import { Button, Divider, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import type { CalculatePriceResponse } from 'api/responseTypes';
import { useCartContext } from 'context/CartContext/CartContext';
import { useEventTracking } from 'hooks/useEventTracking';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { PriceDetailsSkeleton, TotalRowSkeleton } from '../TicketSummary/PriceAndTotalSkeleton';
import { PromoCode } from '../TicketSummary/PromoCode/PromoCode';
import { TermsAndConditions } from '../TicketSummary/TermsAndConditions/TermsAndConditions';
import { TicketPrice } from '../TicketSummary/TicketPrice/TicketPrice';

type CheckoutSummaryProps = {
  termAndConditions: string[];
  isLoading: boolean;
  isCalculatingPrice?: boolean;
  isFreeEvent?: boolean;
  priceCalculation?: CalculatePriceResponse | null;
  onPriceCalculated?: (response: CalculatePriceResponse) => void;
  onPromoCodeRemoved?: () => void;
  onAuthRequired?: () => void;
};

const cardStyle = {
  bg: 'white',
  p: { base: '20px', md: '24px' },
  borderRadius: '16px',
  borderWidth: '1px',
  borderColor: 'dark.D100',
  boxShadow: '0 5px 5px rgba(0, 0, 0, 0.02)',
} as const;

/**
 * Checkout summary sidebar: a price-details card (price / promo / total) and a terms card
 * (bullets / agreement gate / purchase CTA). Event-agnostic — the event header now lives in
 * {@link EventSummaryCard}. The CTA submits the ticket-holder form via its shared form id.
 */
export const CheckoutSummary = ({
  termAndConditions,
  isLoading,
  isCalculatingPrice,
  isFreeEvent,
  priceCalculation,
  onPriceCalculated,
  onPromoCodeRemoved,
  onAuthRequired,
}: CheckoutSummaryProps) => {
  const { tickets, eventId, discountAmount } = useCartContext();
  const [agreed, setAgreed] = useState<boolean>(false);
  const { eventTracking } = useEventTracking();

  const totalPrice =
    priceCalculation?.total ??
    (() => {
      const subtotal = tickets.reduce((acc, curr) => acc + curr.price, 0);
      return Math.max(0, subtotal - (discountAmount ?? 0));
    })();

  const handlePurchaseTicketsClicked = () => {
    eventTracking.cart.purchaseTicketPressed({
      eventId: eventId ?? 0,
      ticketQuantity: tickets.length,
      totalPrice,
    });
  };

  const showSubtotalStrikethrough =
    !isFreeEvent &&
    Boolean(priceCalculation?.subTotal) &&
    priceCalculation?.subTotal !== totalPrice;

  return (
    <VStack spacing='16px' align='stretch' dir='rtl'>
      {!isFreeEvent && (
        <Flex {...cardStyle} direction='column' gap='16px'>
          {/* `start` — under `dir='rtl'` that is the right edge, where the design puts the title.
              `end` would push it to the left. */}
          <Text variant='bodyLargeSemiBold' textAlign='start'>
            <FormattedMessage id='checkout.priceDetails' defaultMessage='Price Details' />
          </Text>

          {isCalculatingPrice ? (
            <PriceDetailsSkeleton />
          ) : (
            <TicketPrice priceCalculation={priceCalculation} />
          )}

          {/* Hairline above the promo row and again below it, so the bill reads as stacked lines
              (`Ticket Zone Checkout Summary.png`). */}
          <Divider borderColor='dark.D100' />

          <PromoCode
            priceCalculation={priceCalculation}
            onPriceCalculated={onPriceCalculated}
            onPromoCodeRemoved={onPromoCodeRemoved}
            onAuthRequired={onAuthRequired}
          />

          <Divider borderColor='dark.D100' />

          <HStack justifyContent='space-between' alignItems='center'>
            {isCalculatingPrice ? (
              <TotalRowSkeleton />
            ) : (
              <>
                {/* Label first, amount opposite — the row order the design uses throughout. */}
                <Text variant='bodyLargeSemiBold'>
                  <FormattedMessage id='checkout.total' defaultMessage='Total' />
                </Text>
                <HStack spacing='8px' alignItems='center'>
                  {showSubtotalStrikethrough && (
                    <Text
                      variant='bodyLargeRegular'
                      color='dark.D400'
                      textDecoration='line-through'
                      fontSize='14px'
                    >
                      {new Intl.NumberFormat().format(priceCalculation?.subTotal ?? 0)} د.ع
                    </Text>
                  )}
                  <Text variant='bodyLargeSemiBold' color='coral.C500'>
                    {new Intl.NumberFormat().format(totalPrice)} د.ع
                  </Text>
                </HStack>
              </>
            )}
          </HStack>
        </Flex>
      )}

      {/* The terms card carries its own blush surface, and its rounded corners close before the
          CTA — so the button sits on a separate surface rather than sharing the card. */}
      {termAndConditions.length > 0 && (
        <TermsAndConditions
          agreed={agreed}
          onAgreedChange={setAgreed}
          termAndConditions={termAndConditions}
        />
      )}

      <VStack spacing='16px' align='stretch'>
        <Button
          variant='coralCta'
          type='submit'
          form='ticket-holder-hook-form'
          isDisabled={isLoading || (termAndConditions.length > 0 && !agreed)}
          onClick={handlePurchaseTicketsClicked}
        >
          {isLoading ? (
            isFreeEvent ? (
              <FormattedMessage
                id='freeEvent.reserveProcessing'
                defaultMessage='Processing reservation...'
              />
            ) : (
              <FormattedMessage id='processing' defaultMessage={'...جاري المعالجة'} />
            )
          ) : isFreeEvent ? (
            <FormattedMessage id='freeEvent.reserveTicket' defaultMessage='Reserve a ticket now' />
          ) : (
            <FormattedMessage id='checkout.purchaseTicket' defaultMessage='Purchase ticket' />
          )}
        </Button>
      </VStack>
    </VStack>
  );
};
